import { el } from "../lib/dom.js";
import { captureExchange } from "../lib/exchange.js";
import { rememberShareTab, storedShareTab, type ShareTab } from "../lib/shareTab.js";
import { linkPanel } from "./shareLink.js";
import { textShare } from "./shareText.js";
import type { ScreenHost, Shell } from "./ui.js";

const GONE = "This item or list is no longer available to share.";

/**
 * One item, as text. Never a link: a link reaches a whole list, and an
 * item's dialog must not hand one out (g73).
 */
export function openShareItem(host: ScreenHost, listId: string, itemId: string, trigger: HTMLElement): void {
  const list = host.store.list(listId);
  const item = host.store.item(itemId);
  if (!list || !item || item.listId !== listId) {
    host.announce(GONE);
    return;
  }
  const text = textShare(host, captureExchange(list, [item]), true, { close: () => popup.dismiss() });
  const popup = host.openPopup({ title: "Share item", body: text.node, focus: text.focus(), returnFocusTo: trigger, onClose: text.dispose });
}

/**
 * A whole list, from its header menu or the Lists overview (g73). Only its
 * owner has it: Link hands out a live view or edit link, Text a copy.
 *
 * Opens on the tab last chosen on this device, Link the first time. The text
 * capture is taken now, whichever tab shows, so switching tabs never changes
 * what Text would send; Link reads nothing until it is shown.
 */
export function openShareList(shell: Shell, listId: string, trigger: HTMLElement): void {
  const list = shell.store.list(listId);
  if (!list) {
    shell.announce(GONE);
    return;
  }
  // Each tab ends in one row: Close, then what the tab is for (g73, g75).
  const dismiss = () => popup.dismiss();
  const text = textShare(shell, captureExchange(list, shell.store.items(listId)), false, { close: dismiss });
  const link = linkPanel({ shell, listId, popup: () => popup, close: dismiss });
  const tabs = shareTabs({ link: link.node, text: text.node }, (tab) => {
    if (tab === "link") link.show();
  });
  const body = el("div", { class: "share-dialog", children: [tabs.list, tabs.panels.link, tabs.panels.text] });
  tabs.select(storedShareTab(), false);
  const popup = shell.openPopup({
    title: "Share list",
    body,
    focus: tabs.selected(),
    returnFocusTo: trigger,
    busy: link.busy,
    onClose: () => { text.dispose(); link.dispose(); },
  });
}

const TAB_LABEL: Record<ShareTab, string> = { link: "Link", text: "Text" };
const TABS: readonly ShareTab[] = ["link", "text"];

/**
 * Two tabs with a roving tabindex: Tab reaches the chosen one, the arrow keys
 * move between them and choose as they go, wrapping, and Home and End go to
 * the ends. Choosing by hand is remembered; opening is not.
 */
function shareTabs(content: Record<ShareTab, HTMLElement>, onSelect: (tab: ShareTab) => void) {
  const buttons = {} as Record<ShareTab, HTMLButtonElement>;
  const panels = {} as Record<ShareTab, HTMLElement>;
  for (const tab of TABS) {
    buttons[tab] = el("button", {
      class: "share-tab",
      text: TAB_LABEL[tab],
      attrs: { type: "button", role: "tab", id: `share-tab-${tab}`, "aria-controls": `share-panel-${tab}` },
      on: { click: () => select(tab, true) },
    });
    panels[tab] = el("div", {
      class: "share-panel",
      attrs: { role: "tabpanel", id: `share-panel-${tab}`, "aria-labelledby": `share-tab-${tab}` },
      children: [content[tab]],
    });
  }
  let current: ShareTab = "link";
  function select(tab: ShareTab, chosen: boolean): void {
    current = tab;
    for (const each of TABS) {
      const on = each === tab;
      buttons[each].setAttribute("aria-selected", String(on));
      buttons[each].tabIndex = on ? 0 : -1;
      // The chosen panel alone decides the sheet's height (g75): the other is
      // `hidden`, out of the layout, the focus order and the accessibility
      // tree, and still mounted, so what it holds -- the format, Include
      // completed, the capture, the link's state -- is there when it returns.
      panels[each].dataset.current = String(on);
      panels[each].hidden = !on;
      panels[each].inert = !on;
    }
    if (chosen) rememberShareTab(tab);
    onSelect(tab);
  }
  const list = el("div", {
    class: "share-tabs",
    attrs: { role: "tablist", "aria-label": "Share as" },
    children: TABS.map((tab) => buttons[tab]),
    on: {
      keydown: (raw) => {
        const event = raw as KeyboardEvent;
        const at = TABS.indexOf(current);
        const next = event.key === "ArrowRight" ? TABS[(at + 1) % TABS.length]
          : event.key === "ArrowLeft" ? TABS[(at + TABS.length - 1) % TABS.length]
          : event.key === "Home" ? TABS[0]
          : event.key === "End" ? TABS[TABS.length - 1]
          : undefined;
        if (!next) return;
        event.preventDefault();
        select(next, true);
        buttons[next].focus();
      },
    },
  });
  return { list, panels, select, selected: () => buttons[current] };
}
